import { useState, useRef, type KeyboardEvent } from 'react';
import type { ShakedownInputType, ShakedownContext, AnalyzeShakedownRequest } from '../lib/api';

interface ShakedownInputProps {
  onSubmit: (request: AnalyzeShakedownRequest) => void;
  isLoading: boolean;
}

const INPUT_TYPES: { type: ShakedownInputType; label: string }[] = [
  { type: 'text', label: 'Text' },
  { type: 'lighterpack', label: 'LighterPack' },
  { type: 'csv', label: 'CSV' },
];

const ACTIVITY_OPTIONS = [
  { value: '', label: 'Not specified' },
  { value: 'hiking', label: 'Hiking' },
  { value: 'backpacking', label: 'Backpacking' },
  { value: 'wild_camping', label: 'Wild Camping' },
  { value: 'winter_mountaineering', label: 'Winter Mountaineering' },
  { value: 'day_hiking', label: 'Day Hiking' },
];

const ACCOMMODATION_OPTIONS = [
  { value: '', label: 'Not specified' },
  { value: 'wild_camping', label: 'Wild Camping' },
  { value: 'mixed', label: 'Mixed (camping + hostels)' },
  { value: 'hostels', label: 'Hostels/B&Bs Only' },
];

const EXAMPLE_TEXT = `MSR Hubba Hubba NX 1700g
Western Mountaineering Ultralite 850g
Thermarest NeoAir XLite 340g
Osprey Exos 58 1100g
Sawyer Squeeze 90g
BRS 3000T Stove 25g
Toaks 750ml Pot 115g`;

const LIGHTERPACK_PLACEHOLDER = 'https://lighterpack.com/r/your-list-id';

const CSV_EXAMPLE = `name,weight_g,category
MSR Hubba Hubba NX,1700,shelter
Sleeping Bag,850,sleep
Sleeping Pad,340,sleep`;

export function ShakedownInput({ onSubmit, isLoading }: ShakedownInputProps) {
  const [inputType, setInputType] = useState<ShakedownInputType>('text');
  const [content, setContent] = useState('');
  const [showOptions, setShowOptions] = useState(false);
  const [activity, setActivity] = useState('');
  const [accommodation, setAccommodation] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const handleTabChange = (type: ShakedownInputType) => {
    setInputType(type);
    setContent('');
    setValidationError(null);
  };

  const handleTabKeyDown = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      const nextIndex = (index + 1) % INPUT_TYPES.length;
      tabRefs.current[nextIndex]?.focus();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      const prevIndex = (index - 1 + INPUT_TYPES.length) % INPUT_TYPES.length;
      tabRefs.current[prevIndex]?.focus();
    }
  };

  const validateLighterPackUrl = (url: string): boolean => {
    const pattern = /^https?:\/\/(www\.)?lighterpack\.com\/r\/[a-zA-Z0-9]+$/;
    return pattern.test(url);
  };

  const handleSubmit = () => {
    if (!content.trim()) return;

    // Validate LighterPack URL
    if (inputType === 'lighterpack' && !validateLighterPackUrl(content.trim())) {
      setValidationError('Please enter a valid LighterPack URL (e.g., https://lighterpack.com/r/abc123)');
      return;
    }

    setValidationError(null);

    const context: ShakedownContext | undefined =
      activity || accommodation
        ? { activity: activity || undefined, accommodation: accommodation || undefined }
        : undefined;

    onSubmit({
      type: inputType,
      content: content.trim(),
      context,
    });
  };

  const handleTryExample = () => {
    if (inputType === 'text') {
      setContent(EXAMPLE_TEXT);
    } else if (inputType === 'csv') {
      setContent(CSV_EXAMPLE);
    }
  };

  const getPlaceholder = (): string => {
    switch (inputType) {
      case 'text':
        return 'Paste your gear list here...\n\nExample:\nMSR Hubba Hubba 1700g\nSleeping Bag 850g\nBackpack 1100g';
      case 'lighterpack':
        return LIGHTERPACK_PLACEHOLDER;
      case 'csv':
        return 'Paste CSV with columns: name, weight_g (or weight_oz), category (optional)';
      default:
        return '';
    }
  };

  const isSubmitDisabled = !content.trim() || isLoading;

  return (
    <div className="w-full max-w-2xl mx-auto">
      {/* Input Type Tabs */}
      <div role="tablist" className="flex border-b border-neutral-800 mb-4">
        {INPUT_TYPES.map((item, index) => (
          <button
            key={item.type}
            ref={(el) => { tabRefs.current[index] = el; }}
            role="tab"
            aria-selected={inputType === item.type}
            aria-controls={`tabpanel-${item.type}`}
            tabIndex={inputType === item.type ? 0 : -1}
            onClick={() => handleTabChange(item.type)}
            onKeyDown={(e) => handleTabKeyDown(e, index)}
            disabled={isLoading}
            className={`px-4 py-2 text-sm font-medium transition-colors relative disabled:opacity-50 ${
              inputType === item.type
                ? 'text-orange-500'
                : 'text-neutral-400 hover:text-white'
            }`}
          >
            {item.label}
            {inputType === item.type && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-500" />
            )}
          </button>
        ))}
      </div>

      {/* Input Panel */}
      <div
        role="tabpanel"
        id={`tabpanel-${inputType}`}
        aria-labelledby={`tab-${inputType}`}
        className="space-y-4"
      >
        {inputType === 'lighterpack' ? (
          <input
            type="url"
            value={content}
            onChange={(e) => {
              setContent(e.target.value);
              setValidationError(null);
            }}
            placeholder={getPlaceholder()}
            disabled={isLoading}
            className="w-full px-4 py-3 bg-neutral-900 border border-neutral-700 rounded-lg text-white placeholder-neutral-500 outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
          />
        ) : (
          <textarea
            value={content}
            onChange={(e) => {
              setContent(e.target.value);
              setValidationError(null);
            }}
            placeholder={getPlaceholder()}
            disabled={isLoading}
            rows={10}
            className="w-full px-4 py-3 bg-neutral-900 border border-neutral-700 rounded-lg text-white placeholder-neutral-500 outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 disabled:opacity-50 disabled:cursor-not-allowed resize-y font-mono text-sm"
          />
        )}

        {/* Validation Error */}
        {validationError && (
          <p className="text-red-400 text-sm" role="alert">
            {validationError}
          </p>
        )}

        {/* Example Button */}
        {inputType !== 'lighterpack' && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-neutral-500">Need an example?</span>
            <button
              type="button"
              onClick={handleTryExample}
              disabled={isLoading}
              className="text-orange-500 hover:text-orange-400 disabled:opacity-50"
            >
              Try example gear list
            </button>
          </div>
        )}

        {/* Context Options Toggle */}
        <div>
          <button
            type="button"
            onClick={() => setShowOptions(!showOptions)}
            className="flex items-center gap-2 text-sm text-neutral-500 hover:text-neutral-300 transition-colors"
          >
            <svg
              className={`w-4 h-4 transition-transform ${showOptions ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
            <span>Trip options (optional)</span>
          </button>

          {/* Context Options Panel */}
          {showOptions && (
            <div className="mt-4 p-4 bg-neutral-900/50 border border-neutral-800 rounded-lg space-y-4 animate-fadeIn">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="activity" className="block text-xs text-neutral-500 uppercase tracking-wider mb-2">
                    Activity
                  </label>
                  <select
                    id="activity"
                    value={activity}
                    onChange={(e) => setActivity(e.target.value)}
                    disabled={isLoading}
                    className="w-full px-3 py-2 bg-neutral-800 border border-neutral-700 rounded-lg text-white text-sm outline-none focus:border-orange-500 disabled:opacity-50"
                  >
                    {ACTIVITY_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="accommodation" className="block text-xs text-neutral-500 uppercase tracking-wider mb-2">
                    Accommodation
                  </label>
                  <select
                    id="accommodation"
                    value={accommodation}
                    onChange={(e) => setAccommodation(e.target.value)}
                    disabled={isLoading}
                    className="w-full px-3 py-2 bg-neutral-800 border border-neutral-700 rounded-lg text-white text-sm outline-none focus:border-orange-500 disabled:opacity-50"
                  >
                    {ACCOMMODATION_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <p className="text-xs text-neutral-500">
                Adding context helps us identify missing essentials for your specific trip.
              </p>
            </div>
          )}
        </div>

        {/* Submit Button */}
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
          className="w-full px-6 py-3.5 bg-orange-500 hover:bg-orange-400 disabled:bg-neutral-700 disabled:cursor-not-allowed text-black font-semibold rounded-lg transition-all duration-200 flex items-center justify-center gap-2 shadow-lg shadow-orange-500/20 hover:shadow-orange-500/30"
        >
          {isLoading ? (
            <>
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
              <span>Analyzing your gear...</span>
            </>
          ) : (
            <>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                />
              </svg>
              <span>Analyze Gear List</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
}
